import { forwardRef } from "react";
import { cn } from "../lib/cn";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Surface container. Bordered by default with a mode-aware elevation
 * (imperceptible in light, a real lift off the near-black canvas in dark).
 * No default padding — compose with CardHeader / CardContent / CardFooter,
 * or pad directly via className.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "overflow-clip rounded-xl border border-border-default bg-surface-default p-0 shadow-card",
          className,
        )}
        {...props}
      />
    );
  },
);
Card.displayName = "Card";

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Card top zone — title/action row with top inset padding. */
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-between gap-2 px-4 pt-4",
          className,
        )}
        {...props}
      />
    );
  },
);
CardHeader.displayName = "CardHeader";

export interface CardContentProps
  extends React.HTMLAttributes<HTMLDivElement> {}

/** Card body zone with bottom inset padding. */
export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn("px-4 pb-4", className)} {...props} />;
  },
);
CardContent.displayName = "CardContent";

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Card bottom action zone — horizontal row. */
export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex gap-2 px-4 pb-4", className)}
        {...props}
      />
    );
  },
);
CardFooter.displayName = "CardFooter";
